import React, { useState, useRef, useEffect } from "react";
import { useKeyPress } from "react-use";
import { clamp, indexOf } from "lodash";
import { ColorSelect } from "@showitinc/showit-wc";

import AppConstants from "../../../constants/AppConstants";
import When from "../../utils/When";
import Picker from "./Picker";
import { hexToRgb, ColorParts } from "../../../utils/ColorUtils";
import { setupHandleWheel } from "../../../utils/InputUtils";
import SiteActions from "../../../actions/SiteActions";
import PickerModal from "./PickerModal";
import uiPrefix from "../../../utils/UiPrefixUtils";

const { DESELECT_PALETTE, DEFAULT_OPACITY, DEFAULT_PALETTE, TRANSPARENT } = AppConstants;
const BASE_CLASS = "si-c-colorpicker";

// keys
const ARROW_UP = "ArrowUp";
const ARROW_DOWN = "ArrowDown";
const ENTER_KEY = "Enter";
const ESCAPE_KEY = "Escape";

const DELAY_CANCEL = "cancel";
const DELAY_CONFIRM = "confirm";
const DELAY_RESET = "reset";

const clampAlpha = (value) => clamp(value, 0, DEFAULT_OPACITY);
const isPaletteColor = (color) => color && color.startsWith("colors-");

const getDisplayColor = (paletteOptions, originalColor, currentColor) =>
  isPaletteColor(originalColor)
    ? paletteOptions[
        (originalColor.includes(":")
          ? originalColor.slice(0, originalColor.indexOf(":"))
          : originalColor
        ).slice(-1)
      ]
    : currentColor;

const ColorPicker = (props) => {
  const {
    color,
    context,
    palette = DEFAULT_PALETTE,
    paletteIndex = DESELECT_PALETTE,
    onColorChange,
    disabled,
    height,
    width,
    showAlpha = true,
    showPalette = true,
    showPaletteOnly,
    label,
    originalColor,
  } = props;

  const parts = ColorParts.fromString(color);
  const { color: propColor, alpha: propAlpha = DEFAULT_OPACITY } = parts;

  const [showPicker, setShowPicker] = useState(false);
  const [pickerVisible, setPickerVisible] = useState(false);
  const [initialColor, setInitialColor] = useState();
  const [initialPaletteIndex, setInitialPaletteIndex] = useState();
  const [baseColor, setBaseColor] = useState();
  const [alpha, setAlpha] = useState(DEFAULT_OPACITY);
  const [delayState, addDelay] = useState("");
  const [enterPressed] = useKeyPress(ENTER_KEY);
  const [escapePressed] = useKeyPress(ESCAPE_KEY);
  const pickerContainerRef = useRef(null);

  useEffect(() => {
    const parts = ColorParts.fromString(color);
    const { color: propColor, alpha: propAlpha = DEFAULT_OPACITY } = parts;
    setBaseColor(propColor);
    setAlpha(propAlpha);
  }, [color]);

  const isTransparent = ColorParts.isTransparent({ color: propColor, alpha: propAlpha });
  const previewStyle = {};

  // inserts default update props so to an update object so only relevant props need to be passed
  const formattedUpdate = (updateProps = { baseColor, alpha, paletteIndex }, addUndo = false) => {
    const {
      baseColor: updatePropColor = baseColor,
      alpha: updatePropAlpha = alpha,
      paletteIndex: updatePaletteIndex = paletteIndex,
    } = updateProps;
    return {
      baseColor: updatePropColor,
      alpha: updatePropAlpha,
      paletteIndex: updatePaletteIndex,
      addUndo,
    };
  };

  const reset = (colorProp = initialColor) => {
    const { color: resetColor, alpha: resetAlpha = DEFAULT_OPACITY } =
      ColorParts.fromString(colorProp);
    setBaseColor(resetColor);
    setAlpha(resetAlpha);
  };

  const _onOpenPicker = (e) => {
    if (disabled) {
      return;
    }

    if (!showPicker) {
      setInitialColor(color);
      setInitialPaletteIndex(paletteIndex);
      reset(color);
      setShowPicker(true);
    }
  };

  const _onCancelSelection = () => {
    const { color: originalColor, alpha: originalAlpha = DEFAULT_OPACITY } =
      ColorParts.fromString(initialColor);

    onColorChange(
      initialColor,
      formattedUpdate({
        baseColor: originalColor,
        alpha: originalAlpha,
        paletteIndex: initialPaletteIndex,
      }),
      true
    );

    setShowPicker(false);
    setPickerVisible(false);
    reset();
  };

  const _alphaChangeHandler = (e, newValue) => {
    e.persist && e.persist();
    const newAlpha = clampAlpha(newValue ?? parseInt(e.target.value, 10));
    setAlpha(newAlpha);
    onColorChange(
      ColorParts.toString({ color: baseColor, alpha: newAlpha }),
      formattedUpdate({
        alpha: newAlpha,
      })
    );
  };

  const _alphaChangeInputHandler = (addUndo) => (e) => {
    const newValue = parseInt(e.detail.value);
    const newAlpha = clampAlpha(newValue);
    setAlpha(newAlpha);
    onColorChange(
      ColorParts.toString({ color: baseColor, alpha: newAlpha }),
      formattedUpdate({
        alpha: newAlpha,
      }),
      addUndo
    );
  };

  // const _blurAlphaHandler = (e) => {
  //   const newAlpha = clampAlpha(alpha, 0, 100);
  //   onColorChange(
  //     ColorParts.toString({ color: baseColor, alpha: newAlpha }),
  //     formattedUpdate({
  //       alpha: newAlpha,
  //     }),
  //     true
  //   );
  // };

  // const _onAlphaArrowPress = (event) => {
  //   if ([ARROW_DOWN, ARROW_UP].includes(event.key)) {
  //     event.stopPropagation();
  //     event.preventDefault();
  //     const modifiedEvent = { ...event, detail: event.key === ARROW_UP ? -1 : 1 };
  //     const wheelCallback = (newValue, e) => _alphaChangeHandler(e, newValue);
  //     return setupHandleWheel({}, wheelCallback, propAlpha)(modifiedEvent);
  //   }
  // };

  const _onConfirmColorSelection = () => {
    setShowPicker(false);
    setPickerVisible(false);
    onColorChange(ColorParts.toString({ color: baseColor, alpha }), formattedUpdate(), true);
  };

  // const getHandleWheelCallback = () => {
  //   const wheelCallback = (newValue, e) => _alphaChangeHandler(e, newValue);
  //   return setupHandleWheel({}, wheelCallback, propAlpha);
  // };

  useEffect(() => {
    escapePressed && showPicker && addDelay(DELAY_CANCEL);
  }, [escapePressed]);

  useEffect(() => {
    enterPressed && showPicker && addDelay(DELAY_CONFIRM);
  }, [enterPressed]);

  // listen for undo redo actions and set a delayed reset flag on either
  useEffect(() => {
    const listenActions = [SiteActions.undo, SiteActions.redo];
    const delayResetCB = () => addDelay(DELAY_RESET);
    const unsubscribers = listenActions.map((action) => action.listen(delayResetCB));

    // add listener and unsetter for theme panel. Refreshes color inputs with replaced theme.
    document.addEventListener("resetThemePanel", delayResetCB);
    unsubscribers.push(() => document.removeEventListener("resetThemePanel", delayResetCB));

    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, []);

  // delays execution allowing for props to trickle back through the stack before firing
  useEffect(() => {
    if (delayState.length) {
      delayState === DELAY_CANCEL && _onCancelSelection();
      delayState === DELAY_CONFIRM && _onConfirmColorSelection();
      delayState === DELAY_RESET && reset(color);
      addDelay("");
    }
  }, [delayState]);

  const containerStyle = { position: "relative" };
  width && (containerStyle.width = width);
  height && (containerStyle.height = height);

  let previewContent = null;
  let previewClass = `${BASE_CLASS}__preview`;
  if (baseColor !== null && !isTransparent) {
    const { r, g, b } = hexToRgb(propColor);
    previewStyle.backgroundColor = `rgba(${r}, ${g}, ${b}, ${propAlpha * 0.01})`;
    if (paletteIndex >= 0) {
      previewContent = (
        <div
          data-e2e={uiPrefix(`color-palette-swatch-number-${paletteIndex + 1}`)}
          style={{ height: "100%", width: "100%", paddingTop: 4 }}
        >
          <span className={` ${BASE_CLASS}__swatch-number`}>{paletteIndex + 1}</span>
        </div>
      );
    }
  } else {
    previewClass += ` ${BASE_CLASS}__preview--transparent`;
    previewContent = "transparent";
  }
  const paletteOptions = [...palette];
  !showPaletteOnly && paletteOptions.push(TRANSPARENT);

  const displayColor = getDisplayColor(paletteOptions, originalColor, propColor);

  return (
    <>
      <When it={showPicker}>
        <PickerModal
          onConfirm={_onConfirmColorSelection}
          pickerVisible={pickerVisible}
          pickerContainerRef={pickerContainerRef}
          setPickerVisible={setPickerVisible}
          showPicker={showPicker}
        >
          <Picker
            alpha={propAlpha}
            color={propColor}
            onCancel={_onCancelSelection}
            onColorChange={onColorChange}
            onConfirm={_onConfirmColorSelection}
            paletteIndex={paletteIndex}
            paletteOptions={paletteOptions}
            setAlpha={setAlpha}
            setBaseColor={setBaseColor}
            setPickerVisible={setPickerVisible}
            setShowPicker={setShowPicker}
            showAlpha={showAlpha}
            showPalette={showPalette}
            showPaletteOnly={showPaletteOnly}
          />
        </PickerModal>
      </When>
      <When it={showAlpha}>
        <ColorSelect
          swatchShape={isPaletteColor(originalColor) ? "square" : "circle"}
          ref={pickerContainerRef}
          label={label}
          disabled={disabled}
          value={`${displayColor}:${propAlpha}`}
          onSIToggleColorSwatch={_onOpenPicker}
          onSIInput={_alphaChangeInputHandler(false)}
          onSIBeforeChange={_alphaChangeInputHandler(true)}
          context={context}
        />
      </When>
    </>
  );
};

export default ColorPicker;
